import { Box, Paper, Stack, Typography } from "@mui/material";
import { EMPTY_DISPLAY } from "../utils/format";
import { EmptyAlert } from "./EmptyAlert";
import { MatchBasisLegend } from "./registryTags/MatchBasisLegend";
import { MatchBasisMark } from "./registryTags/MatchBasisMark";
import { RegistryTagChip } from "./registryTags/RegistryTagChip";
import type { CorporateRegistryTag, RegistryTagSource } from "../types";

interface Props {
  tags: CorporateRegistryTag[];
}

type CardSource = RegistryTagSource & {
  card: NonNullable<RegistryTagSource["card"]>;
};

// タグを前回のまま残した間は、カードの行が0件で返ることがある。そのときはタグだけを出す
const hasCard = (source: RegistryTagSource): source is CardSource =>
  source.card !== null && source.card.rows.length > 0;

/**
 * 名簿1つ分のカード（番号・行政庁・年度など）。右上に紐付けの方法のマーク、下に基準日を出す
 * @param props.source 名簿
 * @returns カード
 */
const SourceCard: React.FC<{ source: CardSource }> = ({ source }) => {
  const { rows, total } = source.card;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Typography fontWeight="bold">{source.sourceLabel}</Typography>
          <MatchBasisMark basis={source.matchBasis} />
        </Stack>
        {rows.map((row, index) => (
          <Box
            // 値が同じ行はまとめ済みなので、値の組で行を区別できる
            key={row.map((item) => item.value).join("|")}
            component="dl"
            sx={{
              m: 0,
              display: "grid",
              gridTemplateColumns: "max-content 1fr",
              columnGap: 1.5,
              rowGap: 0.25,
              pt: index ? 1 : 0,
              borderTop: index ? 1 : 0,
              borderColor: "divider",
            }}
          >
            {row.map((item) => (
              <Box key={item.label} sx={{ display: "contents" }}>
                <Typography
                  component="dt"
                  variant="body2"
                  color="text.secondary"
                >
                  {item.label}
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                  {item.value || EMPTY_DISPLAY}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
        {total > rows.length ? (
          <Typography variant="caption" color="text.secondary">
            ほか {total - rows.length} 件
          </Typography>
        ) : null}
        {source.asOf ? (
          <Typography variant="caption" color="text.secondary">
            基準日 {source.asOf}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
};

/**
 * 詳細の「公表情報」タブ。付いたタグを並べ、その下に番号を出せる名簿のカードを並べる。
 * カードを出さない名簿（規約上はっきりしないもの）は、タグだけを出す
 * @param props.tags 法人に付いたタグ
 * @returns タブの中身
 */
export const PublicRegistryTab: React.FC<Props> = ({ tags }) => {
  if (!tags.length) {
    return <EmptyAlert>この法人が載っている公表情報はありません。</EmptyAlert>;
  }
  const cards = tags.flatMap((tag) => tag.sources.filter(hasCard));
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {tags.map((tag) => (
          <RegistryTagChip key={tag.code} tag={tag} />
        ))}
      </Stack>
      <MatchBasisLegend />
      {cards.length ? (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          }}
        >
          {cards.map((source) => (
            <SourceCard key={source.source} source={source} />
          ))}
        </Box>
      ) : null}
    </Stack>
  );
};
